// Shared sync primitives used by every offline-sync path -- the foreground
// Stimulus controller (field_sync_controller.js), the background-sync
// service worker (field_service_worker.js), and the queue modules
// (field_upload_sync.js, field_image_queue.js). Kept as its own leaf module
// (no imports from field_upload_sync/field_image_queue) so none of those
// files need to import each other and risk a cycle.
//
// Three concerns live here (cards #2417, #2418, #2419, #2421, #2844):
//
//   1. classifySyncResponse -- turns a completed fetch Response into one of
//      four sync outcomes ("success" | "reauth-required" | "failed" | "retry"),
//      applied consistently by response replay, response completion, and
//      image upload.
//
//   2. SYNC_LOCK_NAME -- the fixed Web Locks API name both the foreground
//      sync routine and the background-sync event handler request, so only
//      one sync runs at a time per user/device.
//
//   3. upsertCachedPlace / upsertCachedResponse / fetchAndCachePrefetchedResponse
//      -- the shared write side of the prefetch cache, used by both the
//      foreground auto-sync controller and the background push handler.

// Rails routes that mean "you landed on the sign-in page instead of the real
// endpoint" -- the field app's own sign-in (Field::BaseController
// #authenticate_user! -> new_field_session_url, /field/sign_in) and the main
// app's (ApplicationController#authenticate_user! -> new_session_path,
// /session/new, backing the `resource :session` route). Both are followed
// transparently by fetch's default redirect: "follow" -- there is no 401
// here, Rails just redirects an unauthenticated request to HTML.
const SIGN_IN_PATH_PATTERNS = [/\/sign_in(\/|$)/, /\/session\/new(\/|$)/]

function redirectedToSignIn(response) {
  try {
    return SIGN_IN_PATH_PATTERNS.some((re) => re.test(new URL(response.url).pathname))
  } catch {
    return false
  }
}

function hasJsonContentType(response) {
  const contentType = response.headers?.get?.("Content-Type") || ""
  return contentType.includes("application/json")
}

// True when the response body is genuinely JSON. Checks Content-Type first
// (cheap, doesn't consume the body); falls back to an actual parse attempt on
// a clone so a mislabelled-but-real Content-Type header can't produce a false
// positive, and so the caller's own body read is never affected (Response
// bodies can only be consumed once).
export async function looksLikeJson(response) {
  if (hasJsonContentType(response)) return true
  try {
    await response.clone().json()
    return true
  } catch {
    return false
  }
}

// Classify one completed fetch Response into a sync outcome.
//
//   "success"          -- 2xx, and (when expectJson) a genuine JSON body.
//   "reauth-required"  -- a 401; a CSRF-shaped 422 (Rails'
//                         InvalidAuthenticityToken is unhandled by this app
//                         and falls through to the framework's default 422
//                         HTML error page -- no JSON body, unlike every real
//                         validation 422 this API renders); or a response
//                         that landed on the sign-in page instead of the real
//                         endpoint (session expired/rotated mid-flight --
//                         Rails' authenticate_user! redirects rather than
//                         rendering 401 JSON, and fetch follows redirects by
//                         default, so this can arrive as a "successful" 2xx).
//   "failed"           -- a genuine 4xx (real JSON error body) -- a true
//                         validation error, not retried automatically.
//   "retry"            -- 5xx; transient, try again later. (Network errors
//                         are NOT classified here -- callers catch the thrown
//                         fetch rejection themselves and treat that as "retry".)
//
// expectJson: false for endpoints whose NORMAL success response is not JSON
// (e.g. Field::ResponsesController#complete redirects to an HTML page on
// success) -- callers there must rely on redirectedToSignIn rather than body
// shape to catch the reauth case on an outwardly-2xx response.
export async function classifySyncResponse(response, { expectJson = true } = {}) {
  if (response.status === 401) return "reauth-required"
  if (redirectedToSignIn(response)) return "reauth-required"

  if (response.status === 422) {
    return (await looksLikeJson(response)) ? "failed" : "reauth-required"
  }

  if (response.status >= 400 && response.status < 500) return "failed"
  if (response.status >= 500) return "retry"

  if (expectJson && !(await looksLikeJson(response))) return "reauth-required"

  return "success"
}

// Card #3804 -- the value of Api::V1::Surveys::ResultsController#update's
// write_status body flag when the request was accepted (200, genuine current
// server state in the body) but the actual write was skipped as stale by
// skip_stale_result_write?. Generic name (also consumed by card #3797's
// build) -- a plain 200 here previously looked identical to "my write
// landed", so a caller that deleted its local record on any 2xx (e.g.
// replayResponse) silently dropped the only copy of those answers.
export const WRITE_STATUS_SKIPPED_STALE = "skipped_stale"

// Card #4594 -- local-only cached_responses.status overlays, never written by
// the server. Surveys finished offline sat invisibly as whatever status was
// last synced (usually "started") until the queued completion replayed --
// the place/Today/mission-list pages (server-rendered HTML, kept in
// PAGE_CACHE) had no way to tell "done, not yet synced" from "still being
// worked on". These two values let the field app's own cache say so straight
// away, without ever touching the server's Response#status:
//
//   COMPLETED_OFFLINE -- a completion was queued (tap Complete while offline,
//     or the live POST failed after the data itself saved). Set the instant
//     the queue write lands, cleared back to the server's real "ended" the
//     moment sync confirms it (field_upload_sync.js#replayResponse,
//     result_editor_controller.js#processSurveyData).
//   COMPLETION_ATTENTION -- the queued completion was rejected, or exhausted
//     its retry cap, so it will NOT resolve itself. Distinct from
//     COMPLETED_OFFLINE so a stuck completion is visibly different from one
//     still quietly waiting for signal (card #4594 build item 5).
export const LOCAL_STATUS_COMPLETED_OFFLINE   = "completed_offline"
export const LOCAL_STATUS_COMPLETION_ATTENTION = "completion_attention"

// Fixed Web Locks API name (Card #2421). Both the foreground sync routine
// (field_sync_controller.js) and the background Sync event handler
// (field_service_worker.js) request this SAME lock before running their sync
// loop, so a tab actively syncing and a background-sync event firing at the
// same moment can never both flush the same queued record concurrently.
export const SYNC_LOCK_NAME = "win-field-sync"

// ── cached_responses / cached_places upsert helpers ─────────────────────────
//
// Shared write-side of the prefetch cache (Api::V1::Field::PrefetchController's
// response shape) -- both the foreground auto-sync controller
// (field_auto_sync_controller.js) and the background push handler
// (field_service_worker.js, card #2844) upsert into the SAME two Dexie tables
// from the SAME response payload shape, so this lives here once rather than
// twice.

// Upserts one place object (as embedded on a prefetch response) into
// cached_places, deduplicated by placeId. No-op if the place has no id.
export async function upsertCachedPlace(db, place) {
  if (!place?.id) return

  await db.cached_places.put({
    placeId:          place.id,
    name:             place.name,
    latitude:         place.latitude,
    longitude:        place.longitude,
    address:          place.address,
    bad_connectivity: place.bad_connectivity || false,
    clientId:         place.client_id ?? null,
    clientName:       place.client_name || null
  })
}

// True when a result_json question's value still holds an unresolved
// blob:https://field.winontheshelf.com/... placeholder anywhere inside it --
// the browser's own local Object URL for a not-yet-uploaded photo, standing
// in until the real upload lands and the server rewrites it to a signed
// Active Storage URL (card #3797, incident #3796, ADR-0054). Walks the same
// three shapes swap_image_value (ResultsController) and patch_blob_slot
// (Files::FilesController#substitute_blob_url!) already walk server-side: a
// plain string, a `{content: ...}` hash, or an array of either.
function unresolvedBlobUrls(value) {
  if (typeof value === "string") return value.startsWith("blob:") ? [value] : []
  if (Array.isArray(value)) return value.flatMap(unresolvedBlobUrls)
  if (value && typeof value === "object" && typeof value.content === "string") {
    return value.content.startsWith("blob:") ? [value.content] : []
  }
  return []
}

function containsUnresolvedBlobUrl(value) {
  return unresolvedBlobUrls(value).length > 0
}

// pending_images statuses that mean "this photo is still genuinely queued on
// this device" -- its blob: URL is live, not dead. "failed" is included on
// purpose: syncImagesFor still retries failed records whenever their response
// replays, a tasker can back one up manually from My Photos, and the local
// blob is retained until a confirmed upload (ADR-0048 §3). Letting a server
// value supersede it would drop the answer's only reference to a photo that
// still exists on the device -- keeping local is the no-data-loss default.
const LIVE_IMAGE_STATUSES = ["pending", "uploading", "reauth-required", "failed"]

// The set of blob: URLs that still have a live pending_images record on this
// device (review fix, incidents #3796/#3797). mergeLocalDraftOverServer uses
// it to tell a DEAD stale blob: (no queue record left -- the incident's real
// failure mode, safe to let a resolved server value win) apart from a LIVE
// one (a retake, or a new photo added to a multi-photo question, still
// waiting to upload -- local must win or the answer loses its only reference
// to that photo). localUrl is not indexed, so this filters in memory; the
// queue is small (tens of rows at most). Never throws -- on a Dexie error it
// returns null, which the merge treats as "can't tell, keep local".
export async function liveQueuedBlobUrls(db) {
  try {
    const rows = await db.pending_images
      .where("status").anyOf(LIVE_IMAGE_STATUSES)
      .toArray()
    return new Set(
      rows
        .map(r => r.localUrl)
        .filter(url => typeof url === "string" && url.startsWith("blob:"))
    )
  } catch {
    return null
  }
}

// Local field always wins over the server's copy for any answer key it
// already holds -- ADR-0113 (card #2891), originally scoped to the result
// editor's own read path (result_editor_controller.js's viewSurvey), now
// extended fleet-wide (card #3804) to every writer of cached_responses.
// A response with in-progress answers already cached locally must never
// have that draft overwritten by a stale, partial, or empty server copy
// arriving via prefetch, background push, incremental auto-sync, or a
// manual "Sync for offline" tap -- all of which can race an open editor tab.
//
// Flat, key-by-key precedence with no timestamp comparison (same shape as
// result_editor_controller.js's _mergeWithLocalDraft and rt-client's
// restoreSurveyDataWithFallback), since there is only ever one tasker on one
// device writing to a given response. No local draft yet (first-ever cache
// of this response) -- nothing to protect, the server result is used as-is.
//
// Card #3797 (card #3374 fold-in) -- ONE narrow, structural exception to
// "local always wins": a key whose local value is a dead blob: placeholder
// that the server has since resolved to a real URL. Api::V1::Surveys::Files::
// FilesController#substitute_blob_url! patches a Response's result_json in
// the database when the photo finally uploads -- but that's a server-side
// write only. A device that still holds the OLD cached_responses row for
// this response (it never re-fetched since the fix landed) would otherwise
// re-impose its own stale blob: over the server's fix the next time this
// response opens or re-syncs on that device -- the bug looks fixed on the
// server and never fixed on that device. Detected structurally (does the
// value still literally contain a "blob:" string), never by timestamp or
// intent-guessing:
//   - local still blob:, server ALSO still blob: (mid-flight, upload hasn't
//     landed anywhere yet) -- unchanged, local still wins.
//   - local still blob:, server has no value at all for this key -- unchanged,
//     local still wins (nothing to confirm as "resolved").
//   - local still blob:, server holds a real (non-blob) value, AND none of
//     local's blob: URLs for that key still has a live pending_images record
//     (liveBlobUrls) -- server wins, for that key only. The blob is dead: its
//     queue record was purged or already uploaded, so the server value is
//     the resolved copy.
//   - local still blob:, server holds a real value, but at least one of
//     local's blob: URLs is still live in the queue -- local wins. This is a
//     retake of an already-synced photo, or a new photo added to a
//     multi-photo (array) answer, still waiting to upload. Letting the
//     server's older value win here would drop the tasker's new photo from
//     the answer even though it's about to upload (review fix).
//   - liveBlobUrls is null (the queue couldn't be read) -- local wins, the
//     no-data-loss default.
//   - local holds a genuine, non-blob answer -- never enters this check at
//     all, so it's untouched regardless of what the server holds (a tasker
//     might still be mid-edit).
export function mergeLocalDraftOverServer(localResultJson, serverResultJson, liveBlobUrls = new Set()) {
  if (!localResultJson || Object.keys(localResultJson).length === 0) {
    return serverResultJson || {}
  }

  const server = serverResultJson || {}
  const merged = Object.assign({}, server, localResultJson)

  for (const key of Object.keys(localResultJson)) {
    const localBlobs = unresolvedBlobUrls(localResultJson[key])
    if (localBlobs.length === 0) continue
    if (!Object.prototype.hasOwnProperty.call(server, key)) continue
    if (containsUnresolvedBlobUrl(server[key])) continue
    if (liveBlobUrls === null) continue
    if (localBlobs.some(url => liveBlobUrls.has(url))) continue

    merged[key] = server[key]
  }

  return merged
}

// Upserts one response object (the prefetch API's serialize_response shape)
// into cached_responses. cachedAt is the sync timestamp to stamp on the
// record (synced_at from a full/incremental sync, or the current time for a
// single-response push fetch). Merges any existing local draft over the
// server's resultJson (card #3804) rather than overwriting it outright.
export async function upsertCachedResponse(db, r, cachedAt) {
  const existing = await db.cached_responses.where("responseId").equals(r.id).first()
  const liveBlobUrls = existing?.resultJson ? await liveQueuedBlobUrls(db) : new Set()
  const record = {
    responseId:   r.id,
    placeId:      r.place?.id,
    scheduledAt:  r.schedule_at,
    cachedAt,
    status:       r.status,
    missionName:  r.mission_name,
    // Card #4098 -- sorts the offline place page by priority, like the online
    // one. Not indexed, so no Dexie version bump; a row cached before this
    // field existed reads as blank priority until the one-off backfill runs.
    missionPriority: r.mission_priority ?? null,
    completeUrl:  r.complete_url,
    // Card #4746 -- the GET-able show page (field_response_path), separate
    // from completeUrl above (POST-only). The SW's priority mission page warm
    // pass (_warmPriorityMissionPages in field_service_worker.js) reads this
    // off the cached record so it never GET-fetches the POST-only completion
    // endpoint. Not indexed, so no Dexie version bump -- a row cached before
    // this field existed reads as undefined until the next sync refreshes it,
    // same precedent as missionPriority/surveyDigest above.
    pageUrl:      r.url,
    surveyJson:   r.survey_json,
    // Card #4117 -- which mission this is and the fingerprint of its survey, so
    // a sibling response can lend its survey when this one cannot be fetched
    // (field_borrowed_survey.js). Not indexed. Rows cached before the card
    // have neither until the one-off backfill runs.
    missionId:    r.mission?.id ?? null,
    surveyDigest: r.survey_digest ?? null,
    placeContext: r.place_context || {},
    // Last-visit ranging hints (cards #4031/#3073), cached offline with the response.
    // null when field:ranging_confirm is off. Never merged into resultJson.
    rangingHints: r.ranging_hints ?? null,
    rangingBarcodes: r.ranging_barcodes ?? null,
    // Open allocation/TIO incoming-stock badge (card #4692), same rules.
    rangingIncoming: r.ranging_incoming ?? null,
    resultJson:   mergeLocalDraftOverServer(existing?.resultJson, r.result_json || {}, liveBlobUrls)
  }

  if (existing) {
    await db.cached_responses.update(existing.id, record)
  } else {
    await db.cached_responses.add(record)
  }
}

// Card #4117 -- a prefetch request sent with dedupe_surveys=1 carries each
// distinct survey once, in payload.surveys, and each response only its
// survey_digest. Puts survey_json back on every response so the rest of the
// sync code is unchanged. A response that already has survey_json (an older
// server, or a single-response fetch) is left alone. Returns the payload.
export function expandSharedSurveys(payload) {
  const surveys = payload?.surveys
  if (!surveys) return payload
  for (const r of (payload.responses || [])) {
    if (r.survey_json == null && r.survey_digest && surveys[r.survey_digest] !== undefined) {
      r.survey_json = surveys[r.survey_digest]
    }
  }
  return payload
}

// Card #4117 -- one run at a time for a named job, across every controller
// instance on the page. A Stimulus controller's own `_syncing` flag lives on
// the instance, and Turbo makes a fresh instance each time the page is shown
// again, so a sync started on the old instance was invisible to the new one
// and a second tap ran a second full sync on top of it. This lives at module
// level (shared by every instance in the page's JavaScript) and, where the
// browser has Web Locks, across tabs too.
//
// Returns { ran: false } when the job was already running, otherwise
// { ran: true, value } with whatever fn resolved to (or throws what fn threw).
const runningJobs = new Set()

export async function runSingleFlight(name, fn) {
  if (runningJobs.has(name)) return { ran: false }
  runningJobs.add(name)
  try {
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
      let outcome = { ran: false }
      await navigator.locks.request(`win-field-job-${name}`, { ifAvailable: true }, async (lock) => {
        if (!lock) return
        outcome = { ran: true, value: await fn() }
      })
      return outcome
    }
    return { ran: true, value: await fn() }
  } finally {
    runningJobs.delete(name)
  }
}

// Card #3827 -- writes a status change made online (Start, Complete) into the
// response's existing cached_responses row, so the offline shell and Today
// tab don't keep showing "scheduled" until the next prefetch. Update only:
// with no row there is no survey JSON to open it offline, so nothing is
// invented. Only `status` changes; the local draft (resultJson) is left
// alone (ADR-0113). Returns true when a row was updated.
export async function setCachedResponseStatus(db, responseId, status) {
  const existing = await db.cached_responses.where("responseId").equals(Number(responseId)).first()
  if (!existing) return false
  await db.cached_responses.update(existing.id, { status })
  return true
}

// Fetches one response's prefetch payload and writes it into IndexedDB.
// Used by the field service worker's push handler (card #2844) to warm the
// cache for a specific response off the back of a prefetch push notification
// -- a follow-up request, not embedded in the push payload itself (push size
// limits). Also usable from a page context (e.g. tests), given a fetch impl.
//
// Returns true on a successful cache write, false otherwise. Never throws --
// callers (the SW push handler) log failures themselves; this stays a plain
// data function so it's directly testable without a real push event.
export async function fetchAndCachePrefetchedResponse(db, responseId, { fetchImpl = fetch } = {}) {
  const res = await fetchImpl(`/api/v1/field/prefetch.json?response_id=${encodeURIComponent(responseId)}`, {
    method:      "GET",
    headers:     { "Accept": "application/json" },
    credentials: "include"
  })
  if (!res.ok) return false

  const { responses, synced_at } = await res.json()
  const r = (responses || [])[0]
  if (!r) return false

  await upsertCachedPlace(db, r.place)
  await upsertCachedResponse(db, r, synced_at)
  return true
}
